import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Orbit } from "lucide-react";
import { PageHeader, Panel, NAV } from "@/components/layout/AppShell";
import StrengthGauge from "@/components/StrengthGauge";
import LiveChart from "@/components/LiveChart";
import NewsList from "@/components/NewsList";
import { usePoll } from "@/context/AuthContext";
import { isFlashing, useMarket } from "@/context/MarketContext";
import { actionTone, fmt, toneClasses } from "@/lib/format";

const IDX = ["NIFTY", "BANKNIFTY", "FINNIFTY", "SENSEX"];

function GlobalImpact({ g }) {
  if (!g) return <div className="h-40 animate-pulse" />;
  return (
    <div className="space-y-3" data-testid="global-impact">
      {g.factors.map((f) => (
        <div key={f.symbol} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
          <div>
            <div className="text-sm font-semibold">{f.name}</div>
            <div className="font-data text-xs text-gray-500">{fmt(f.ltp, f.symbol === "USDINR" ? 4 : 2)} <span className={f.chg_pct >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}>{f.chg_pct >= 0 ? "+" : ""}{fmt(f.chg_pct)}%</span></div>
          </div>
          <div className="text-right">
            <div className={`font-data text-sm font-bold ${f.impact_pts >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{f.impact_pts >= 0 ? "+" : ""}{fmt(f.impact_pts)} pts</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{f.effect} on Nifty</div>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between p-3 rounded-lg border border-[#007AFF]/30 bg-[#007AFF]/5">
        <span className="text-sm font-semibold">Net Indian market impact</span>
        <span className="font-data text-sm font-bold" data-testid="net-impact">{g.nifty_impact_pts >= 0 ? "+" : ""}{fmt(g.nifty_impact_pts)} pts · {g.outlook}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { quotes, global, flash } = useMarket();
  const { data: ob } = usePoll("/segment/option-buyer", 4000);
  const { data: intra } = usePoll("/segment/intraday", 4000);
  const top = [...(ob?.items || []), ...(intra?.items || [])].filter((i) => i.action !== "WAIT")
    .sort((a, b) => Math.abs(b.score - 50) - Math.abs(a.score - 50)).slice(0, 6);

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Command Center" title="Market Pulse" sub="Live Strength Scores across indices, global cues and the strongest active signals."
        right={<Link to="/app/immersive" data-testid="open-immersive-btn" className="inline-flex items-center gap-2 rounded-full border border-[#00FF66]/40 text-[#00FF66] px-5 py-2 text-sm font-bold hover:bg-[#00FF66]/10 transition-colors"><Orbit size={16} /> Immersive Mode</Link>} />
      <motion.div initial="h" animate="s" variants={{ s: { transition: { staggerChildren: 0.06 } } }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {IDX.map((s) => (
          <motion.div key={s} variants={{ h: { opacity: 0, y: 12 }, s: { opacity: 1, y: 0 } }} className={`rx-card p-4 ${isFlashing(flash[s]) ? "rx-alert-flash" : ""}`} data-testid={`index-card-${s}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display text-sm font-bold">{s}</div>
                <div className="font-data text-lg mt-1">{fmt(quotes[s]?.ltp)}</div>
                <div className={`font-data text-xs ${(quotes[s]?.chg_pct ?? 0) >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{fmt(quotes[s]?.chg_pct)}%</div>
              </div>
            </div>
            <div className="mt-2 flex justify-center"><StrengthGauge score={quotes[s]?.score ?? 50} size={140} label="Strength" testId={`gauge-${s}`} /></div>
          </motion.div>
        ))}
      </motion.div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Panel className="lg:col-span-2" title="NIFTY 50 · Live with signal overlays" testId="dashboard-chart"><LiveChart symbol="NIFTY" height={380} /></Panel>
        <Panel title="Global cues → Indian market" testId="global-panel"><GlobalImpact g={global} /></Panel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <Panel className="lg:col-span-2" title="Strongest active signals" testId="top-signals">
          <div className="divide-y divide-white/5">
            {top.length === 0 && <div className="text-sm text-gray-500 py-6">Scanning markets…</div>}
            {top.map((i) => (
              <div key={i.symbol + i.instrument} className="flex items-center gap-3 py-3" data-testid={`top-signal-${i.symbol}`}>
                <span className={`font-data text-[11px] font-bold px-2 py-1 rounded-md border w-24 text-center ${toneClasses[actionTone(i.action)]}`}>{i.action}</span>
                <span className="font-data text-sm flex-1 truncate">{i.instrument}</span>
                <span className="font-data text-xs text-gray-400 hidden sm:block">{i.levels.slice(0, 3).map((l) => `${l.k} ${fmt(l.v)}`).join(" · ")}</span>
                <span className="font-data text-sm font-bold w-10 text-right" style={{ color: i.score >= 65 ? "#00FF66" : "#FF3366" }}>{i.score.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Live news" right={<Link to="/app/news" className="text-xs text-gray-400 hover:text-white">All</Link>} testId="news-panel"><NewsList limit={6} compact /></Panel>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {NAV[0].items.slice(1).map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className="rx-card rx-lift p-4 flex items-center gap-3 group" data-testid={`quick-${to.split("/").pop()}`}>
            <Icon size={18} className="text-[#00FF66]" /><span className="text-sm font-semibold flex-1">{label}</span>
            <ArrowUpRight size={14} className="text-gray-600 group-hover:text-white transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
}
